import React, { useState } from "react";
import { Grid, Box, Typography, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ArrowBack, ArrowForward, CheckCircle } from "@mui/icons-material";
import PanToolAltOutlinedIcon from '@mui/icons-material/PanToolAltOutlined';

const borderStyles = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
    minHeight: "620px",
    borderRadius: "30px",
    padding: "52px",
    background:
        "radial-gradient(90.16% 143.01% at 15.32% 21.04%, rgba(165, 239, 255, 0.2) 0%, rgba(110, 191, 244, 0.0447917) 77.08%, rgba(70, 144, 213, 0) 100%)",
    "&::before": {
        content: '""',
        position: "absolute",
        top: "-1px",
        left: "-1px",
        right: "-1px",
        bottom: "-1px",
        borderRadius: "30px",
        padding: "0.85px",
        background:
            "radial-gradient(80.38% 222.5% at -13.75% -12.36%, #98F9FF 0%, rgba(255, 255, 255, 0) 100%), radial-gradient(80.69% 208.78% at 108.28% 112.58%, #EABFFF 0%, rgba(135, 38, 183, 0) 100%)",
        WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
        WebkitMaskComposite: "destination-out",
        maskComposite: "exclude",
        zIndex: -1,
    },

    "@media (max-width: 960px)": { // Tablet and below
        minHeight: "400px",
        padding: "24px",
    },
};

const MainContainer = styled(Box)(() => ({
    background: "#000",
    display: "flex",
    flexDirection: "column",
    padding: "60px 24px",
    position: "relative",
    overflow: "hidden",
    width: "100%",
    margin: "0 auto",
}));

const ContentContainer = styled(Box)(() => ({
    width: "97%",
    maxWidth: "1200px",
    margin: "0 auto",
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
}));

const SolutionSliderView = ({ title, subTitle, data }) => {
    const [activeIndex, setActiveIndex] = useState(0);

    const handleSlideChange = (direction) => {
        if (direction === "prev") {
            setActiveIndex((prevIndex) => (prevIndex === 0 ? data.length - 1 : prevIndex - 1));
        } else {
            setActiveIndex((prevIndex) => (prevIndex === data.length - 1 ? 0 : prevIndex + 1));
        }
    };

    return (
        <MainContainer>
            <ContentContainer>
                <Box sx={{ width: { xs: "100%", md: "70%" }, textAlign: "left", mb: 3 }}>
                    <Typography variant="h2" sx={{
                        background: "linear-gradient(135deg, #6366F1 0%, #3B82F6 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        fontSize: { xs: 24, sm: 32, md: 40 },
                        fontWeight: 700,
                    }}>
                        {title}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },  // Stack on mobile, row on desktop
                        alignItems: { xs: "center", md: "center" },  // Center on mobile, align in line on desktop
                        justifyContent: "space-between",  // Typography left, buttons right on desktop
                        width: "100%",
                        gap: { xs: 2, md: 0 },  // Space between elements on mobile
                        mb: { xs: 2, md: 6 }
                    }}
                >
                    <Typography sx={{
                        color: "#8C8CC2",
                        fontSize: { xs: 14, md: 18 },
                        lineHeight: 1.6,
                        width: { xs: "100%", md: "70%" }
                    }}>
                        {subTitle}
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            gap: 3,
                            width: { xs: "100%", md: "auto" },  // Full width on mobile, auto on desktop
                            justifyContent: { xs: "center", md: "flex-end" },  // Centered on mobile, right on desktop

                        }}
                    >
                        <button onClick={() => handleSlideChange("prev")} className="circular-btn">
                            &lt;
                        </button>
                        <button onClick={() => handleSlideChange("next")} className="circular-btn">
                            &gt;
                        </button>
                    </Box>
                </Box>

                <Grid container spacing={2} alignItems="center">
                    {/* Left: Image */}
                    <Grid item xs={12} sm={12} md={4} display="flex" justifyContent="center">
                        <Box sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "left",
                            textAlign: "left",
                            justifyContent: "space-between",
                            p: "24px",
                            borderRadius: "30px",
                            maxWidth: "400px",
                            minHeight: { xs: "400px", md: "620px" },
                            background: "radial-gradient(90.16% 143.01% at 15.32% 21.04%, rgba(165, 239, 255, 0.2) 0%, rgba(110, 191, 244, 0.0447917) 77.08%, rgba(70, 144, 213, 0) 100%)"
                        }}>
                            <img
                                src={data[activeIndex].imgUrl}
                                alt="Graph"
                                style={{
                                    borderRadius: "12px",
                                    width: "50%",
                                    height: "auto",
                                    objectFit: "cover",
                                    display: "block",
                                }}
                            />
                            <Typography sx={{
                                color: "rgba(165, 239, 255, 0.8)",
                                fontSize: { xs: 18, md: 24 },
                                lineHeight: 1.6,
                                mt: 2
                            }}>
                                {data[activeIndex].title}
                            </Typography>
                            <Typography sx={{
                                color: "#FFFFFF",
                                fontSize: { xs: 14, md: 18 },
                                lineHeight: 1.6,
                                mt: 1
                            }}>
                                {data[activeIndex].subTitle}
                            </Typography>
                        </Box>
                    </Grid>

                    {/* Right: List */}
                    <Grid item xs={12} md={8}>
                        <Box sx={borderStyles}>
                            <Typography sx={{
                                color: "#FFFFFF",
                                fontSize: { xs: 24, md: 30 },
                                fontWeight: 400,
                                lineHeight: 1.6,
                                mb: 6
                            }}>
                                {data[activeIndex].listName}
                            </Typography>

                            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", justifyContent:"space-around", gap: 5}}>
                                {data[activeIndex]?.listItems?.map((item, index) => (
                                    <Box key={index} sx={{
                                        display: "flex",
                                        alignItems: "center", 
                                        gap: 2,
                                        color: "#FFFFFF",
                                        fontSize: { xs: 18, md: 24 }
                                    }}>
                                       <PanToolAltOutlinedIcon sx={{ color: "#61D1FA", transform: "rotate(90deg)" }} />
                                        <Typography>{item}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </ContentContainer>
        </MainContainer>
    );
};

export default SolutionSliderView;
